import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode
} from "react";

import { clearWaveformCache, loadWaveform } from "./waveform";
import * as styles from "./audio-message-player.css";

const PLAYBACK_RATES = [1, 1.5, 2] as const;
const DEFAULT_BAR_COUNT = 36;

export type AudioMessageStatus =
  | "received-unread"
  | "sent"
  | "read"
  | undefined;

export interface AudioMessagePlayerLabels {
  play: string;
  pause: string;
  loading: string;
  error: string;
  retry: string;
  seek: string;
  speed: string;
  unread: string;
  sent: string;
  read: string;
}

export interface AudioMessagePlayerProps {
  source: string;
  duration?: number;
  status?: AudioMessageStatus;
  sentAtLabel?: string;
  labels: AudioMessagePlayerLabels;
  className?: string;
  barCount?: number;
  statusIcons?: {
    sent?: ReactNode;
    read?: ReactNode;
  };
}

export const formatAudioTime = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds)
    ? Math.max(0, Math.floor(seconds))
    : 0;
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(
    2,
    "0"
  )}`;
};

const StatusIndicator = ({
  status,
  labels,
  icons
}: {
  status: AudioMessageStatus;
  labels: AudioMessagePlayerLabels;
  icons?: AudioMessagePlayerProps["statusIcons"];
}) => {
  if (status === "received-unread") {
    return (
      <span
        className={styles.unreadDot}
        aria-label={labels.unread}
        data-testid="audio-message-unread"
      />
    );
  }

  if (status === "sent") {
    return (
      <span
        className={styles.deliveryStatus}
        aria-label={labels.sent}
        data-testid="audio-message-sent"
      >
        {icons?.sent ?? "✓"}
      </span>
    );
  }

  if (status === "read") {
    return (
      <span
        className={styles.readStatus}
        aria-label={labels.read}
        data-testid="audio-message-read"
      >
        {icons?.read ?? "✓✓"}
      </span>
    );
  }

  return null;
};

export const AudioMessagePlayer = ({
  source,
  duration: providedDuration,
  status,
  sentAtLabel,
  labels,
  className,
  barCount = DEFAULT_BAR_COUNT,
  statusIcons
}: AudioMessagePlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [waveform, setWaveform] = useState<number[] | null>(null);
  const [waveformAttempt, setWaveformAttempt] = useState(0);
  const [waveformError, setWaveformError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  const [position, setPosition] = useState(0);
  const [metadataDuration, setMetadataDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState<
    (typeof PLAYBACK_RATES)[number]
  >(PLAYBACK_RATES[0]);

  const duration =
    metadataDuration > 0
      ? metadataDuration
      : Math.max(0, providedDuration ?? 0);
  const progress = duration > 0 ? Math.min(1, position / duration) : 0;

  useEffect(() => {
    let current = true;
    setWaveform(null);
    setWaveformError(false);

    void loadWaveform(source, barCount)
      .then((values) => {
        if (current) setWaveform(values);
      })
      .catch(() => {
        if (current) setWaveformError(true);
      });

    return () => {
      current = false;
    };
  }, [barCount, source, waveformAttempt]);

  useEffect(() => {
    setIsPlaying(false);
    setHasPlaybackStarted(false);
    setIsBuffering(false);
    setPlaybackError(false);
    setPosition(0);
    setMetadataDuration(0);
    setPlaybackRate(PLAYBACK_RATES[0]);
  }, [source]);

  const togglePlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    setPlaybackError(false);
    if (!audio.paused) {
      audio.pause();
      return;
    }

    try {
      await audio.play();
    } catch {
      setPlaybackError(true);
      setIsPlaying(false);
    }
  }, []);

  const seek = useCallback((nextPosition: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = nextPosition;
    setPosition(nextPosition);
    setHasPlaybackStarted(true);
  }, []);

  const cyclePlaybackRate = useCallback(() => {
    const currentIndex = PLAYBACK_RATES.indexOf(playbackRate);
    const nextRate = PLAYBACK_RATES[(currentIndex + 1) % PLAYBACK_RATES.length];
    const audio = audioRef.current;
    if (audio) audio.playbackRate = nextRate;
    setPlaybackRate(nextRate);
  }, [playbackRate]);

  const retryWaveform = useCallback(() => {
    clearWaveformCache(source);
    setWaveformError(false);
    setWaveformAttempt((attempt) => attempt + 1);
  }, [source]);

  const bars = useMemo(
    () => waveform ?? Array.from({ length: barCount }, () => 0.2),
    [barCount, waveform]
  );

  return (
    <div
      className={`${styles.root} ${className ?? ""}`}
      data-testid="audio-message-player"
    >
      <audio
        ref={audioRef}
        src={source}
        preload="metadata"
        playsInline
        onLoadedMetadata={(event) => {
          const nextDuration = event.currentTarget.duration;
          if (Number.isFinite(nextDuration)) setMetadataDuration(nextDuration);
        }}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onPlay={() => {
          setIsPlaying(true);
          setHasPlaybackStarted(true);
        }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsBuffering(true)}
        onCanPlay={() => setIsBuffering(false)}
        onEnded={() => {
          setIsPlaying(false);
          setHasPlaybackStarted(false);
          setPosition(0);
        }}
        onError={() => setPlaybackError(true)}
      />

      <button
        type="button"
        className={styles.playButton}
        onClick={() => void togglePlayback()}
        aria-label={isPlaying ? labels.pause : labels.play}
        data-testid="audio-message-play"
      >
        <span className={isPlaying ? styles.pauseIcon : styles.playIcon} />
      </button>

      <div className={styles.content}>
        <div className={styles.waveform}>
          <div className={styles.bars} aria-hidden="true">
            {bars.map((height, index) => (
              <span
                key={index}
                className={`${styles.bar} ${
                  index / bars.length < progress ? styles.barPlayed : ""
                }`}
                data-testid="audio-message-bar"
                style={
                  {
                    "--audio-bar-height": `${Math.round(height * 100)}%`
                  } as CSSProperties
                }
              />
            ))}
          </div>
          <input
            className={styles.seek}
            type="range"
            min={0}
            max={duration || 0}
            step={0.01}
            value={Math.min(position, duration || 0)}
            disabled={duration <= 0}
            onChange={(event) => seek(Number(event.currentTarget.value))}
            aria-label={labels.seek}
            data-testid="audio-message-seek"
          />
        </div>

        <div className={styles.meta}>
          <span data-testid="audio-message-time">
            {formatAudioTime(hasPlaybackStarted ? position : duration)}
          </span>
          <button
            type="button"
            className={styles.speedButton}
            onClick={cyclePlaybackRate}
            aria-label={labels.speed}
            data-testid="audio-message-speed"
          >
            ×{playbackRate}
          </button>
          {sentAtLabel ? (
            <span className={styles.sentAt}>{sentAtLabel}</span>
          ) : null}
          <StatusIndicator
            status={status}
            labels={labels}
            icons={statusIcons}
          />
        </div>
      </div>

      {!waveform && !waveformError ? (
        <span className={styles.state} role="status">
          {labels.loading}
        </span>
      ) : null}
      {isBuffering ? (
        <span className={styles.state} role="status">
          {labels.loading}
        </span>
      ) : null}
      {waveformError || playbackError ? (
        <span className={styles.error} role="alert">
          {labels.error}
          {waveformError ? (
            <button
              type="button"
              className={styles.retryButton}
              onClick={retryWaveform}
            >
              {labels.retry}
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
};
